import { localeMeta } from "@/lib/seo-head";
import { useTx } from "@/lib/i18n-dict";
import { createFileRoute } from "@tanstack/react-router";
import { BookOpen, Mail, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { Section } from "@/components/site/Section";
import { PrimaryCta } from "@/components/site/Cta";
import { REUSING_CONTENT } from "@/content/pages";
import { alternateLinks } from "@/lib/i18n";
import { SITE } from "@/content/site";

const TITLE = "Reusing content — factory.online";
const DESCRIPTION =
  "What you may do with the articles, guides and illustrations on factory.online — internal use with attribution, and how to get permission for republication.";

export const Route = createFileRoute("/{-$locale}/reusing-content")({
  staticData: { sitemap: true },
  head: ({ params }) => {
    const __m = localeMeta(params, TITLE, DESCRIPTION);
    return {
      meta: [
        { title: __m.title },
        { name: "description", content: __m.description },
        { property: "og:title", content: __m.title },
        { property: "og:description", content: __m.description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: alternateLinks("/reusing-content"),
    };
  },
  component: ReusingContent,
});

const SECTION_ICONS: (typeof BookOpen)[] = [BookOpen, Mail, ShieldCheck];

function ReusingContent() {
  const C = useTx(REUSING_CONTENT);
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-96"
          style={{ background: "var(--gradient-edge)" }}
        />
        <div className="container-page relative py-20 md:py-28">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">
              {C.intro.eyebrow}
            </p>
            <h1 className="mt-3 font-display text-4xl leading-[1.05] font-bold text-balance-tight sm:text-5xl">
              {C.intro.title}
            </h1>
            <p className="mt-6 text-base text-muted-foreground md:text-lg">
              {C.intro.lead}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Guidelines */}
      <Section>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {C.sections.map((section, index) => {
            const Icon = SECTION_ICONS[index % SECTION_ICONS.length]!;
            return (
              <Reveal
                key={section.title}
                delay={index * 60}
                className="flex h-full flex-col rounded-xl border border-border bg-surface/40 p-6"
              >
                <div className="flex size-11 items-center justify-center rounded-full border border-primary/25 bg-primary/10 text-primary">
                  <Icon className="size-5" />
                </div>
                <h2 className="mt-4 font-display text-lg font-semibold text-balance-tight">
                  {section.title}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {section.text}
                </p>
              </Reveal>
            );
          })}
        </div>
      </Section>

      {/* Closing */}
      <Section className="bg-surface/40">
        <Reveal className="rounded-2xl border border-primary/30 bg-surface p-10 text-center shadow-[var(--glow-primary)] md:p-16">
          <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold text-balance-tight md:text-4xl">
            {C.closing.title}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground md:text-lg">
            {C.closing.lead}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href={`mailto:${SITE.email}`}
              className="inline-flex h-11 items-center justify-center rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              {SITE.email}
            </a>
            <PrimaryCta to="/contacts">Talk to a consultant</PrimaryCta>
          </div>
        </Reveal>
      </Section>
    </>
  );
}
